import { useEffect, useState } from 'react'

/**
 * Chart palette for App Analytics V2.
 *
 * Both columns are selected for their own surface — the dark column is the same
 * hues re-stepped for a dark background, not an automatic flip of the light one.
 * Every set used below was run through a colour-blind separation check: the
 * iPhone/Android pair clears CVD delta-E 24.7 (light) / 26.8 (dark), and the
 * five-slot version palette clears 9.1 / 8.4 on adjacent pairs.
 *
 * Series identity is never carried by colour alone — every chart here ships a
 * legend, and the ranked lists repeat the numbers as text.
 */
const PALETTE = {
  light: {
    surface: '#ffffff',
    grid: 'rgba(0, 0, 21, 0.1)',
    text: '#4f5d73',
    textMuted: '#768192',
    series: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4'],
    neutral: '#a9b3c1',
  },
  dark: {
    surface: '#2a303c',
    grid: 'rgba(255, 255, 255, 0.12)',
    text: '#c8ccd4',
    textMuted: '#9da5b1',
    series: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181'],
    neutral: '#6c7581',
  },
}

/** iOS keeps slot 1, Android slot 2, always — colour follows the platform, never its rank. */
export const PLATFORM_META = [
  { key: 'ios', label: 'iPhone (iOS)', slot: 0 },
  { key: 'android', label: 'Android', slot: 1 },
]

/**
 * CoreUI sets data-coreui-theme on <html>. Fall back to the OS preference, then
 * to light. Re-reads on change so a theme toggle repaints the charts.
 */
export const useChartTheme = () => {
  const read = () => {
    if (typeof document === 'undefined') return 'light'
    const attr = document.documentElement.getAttribute('data-coreui-theme')
    if (attr === 'dark' || attr === 'light') return attr
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return 'light'
  }

  const [mode, setMode] = useState(read)

  useEffect(() => {
    const update = () => setMode(read())

    const observer = new MutationObserver(update)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-coreui-theme'],
    })

    const media = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null
    if (media && media.addEventListener) media.addEventListener('change', update)

    return () => {
      observer.disconnect()
      if (media && media.removeEventListener) media.removeEventListener('change', update)
    }
  }, [])

  return PALETTE[mode]
}

/**
 * Shared Chart.js options. Gridlines are hairline and recessive, axis text uses
 * text tokens rather than the series colour, and tooltips are on by default.
 */
export const baseChartOptions = (theme, overrides = {}) => ({
  maintainAspectRatio: false,
  responsive: true,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        color: theme.text,
        usePointStyle: true,
        pointStyle: 'circle',
        boxWidth: 8,
        padding: 16,
      },
    },
    tooltip: {
      backgroundColor: theme.surface,
      titleColor: theme.text,
      bodyColor: theme.text,
      borderColor: theme.grid,
      borderWidth: 1,
      padding: 10,
      displayColors: true,
    },
    ...(overrides.plugins || {}),
  },
  ...overrides,
})

/** Recessive axis styling, applied to whichever axes a chart declares. */
export const axis = (theme, extra = {}) => ({
  grid: { color: theme.grid, drawBorder: false, lineWidth: 1 },
  ...extra,
  // Merged last so a caller overriding `ticks` cannot accidentally drop the
  // text colour and leave the axis labels at Chart.js's default near-black.
  ticks: { color: theme.textMuted, ...(extra.ticks || {}) },
})

export default PALETTE
